import React, { useRef, useEffect, useState, useCallback } from 'react';
import './NewsUpdate.scss';
import getTranslate from '../../translator.js';
import { getNewsUpdateUrl, getCopyNewsUpdateUrl, getDeleteNewsUpdateUrl, getToogleViewedNewsUpdateUrl, getUpdateNewsLocationUrl, getUpdateNewsContentUrl } from '../../api.js';
import { TOKEN, LANGUAGE_CODES, COUNTRY_CODES } from '../../constants.js';

const NewsUpdate = ({ onShareClicked, locations, selectedData, languageCode, countryCode, locationId }) => {
  const sourceRef = useRef(null);
  const titleRef = useRef(null);
  const descriptionRef = useRef(null);
  const linkRef = useRef(null);

  const [newsUpdates, setNewsUpdates] = useState([]);
  const [viewFilter, setViewFilter] = useState('unviewed'); // 'all', 'viewed', 'unviewed'
  const [selectedAreaId, setSelectedAreaId] = useState('0'); // State to store selected areaId
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingNewsId, setEditingNewsId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [editingDescription, setEditingDescription] = useState('');
  const [savingNewsId, setSavingNewsId] = useState(null);

  const getLocationName = (areaId) => {
    if (!countryCode) return 'Country not selected';
    if (!areaId) return 'Area not selected';

    if( areaId == 0 ) return 'New Zealand';
    if( areaId == 201 ) return 'Australia';
    if( areaId == 301 ) return 'Canada';

    const location = locations.find(loc =>
      loc.id === areaId && loc.countryCode.toLowerCase() === countryCode.toLowerCase()
    );

    if (!location) {
      console.warn(`Location not found for ${countryCode.toUpperCase()}-${areaId}`);
      return countryCode.toUpperCase();
    }

    return languageCode === 'ko' ? location.name_ko : location.name_en;
  };

  const fetchNewsUpdates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Convert viewFilter to boolean value for the API
      let viewedParam;
      if (viewFilter === 'viewed') viewedParam = true;
      else if (viewFilter === 'unviewed') viewedParam = false;
      // if viewFilter === 'all', viewedParam remains undefined
      // Set a very high limit to get all items
      const limit = 1000;
      const url = getNewsUpdateUrl(locationId, viewedParam, 1, limit);

      const response = await fetch(url, {
        headers: {
          'Authorization': TOKEN
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseJson = await response.json();
      let newsData = responseJson.data || [];
      const totalItems = responseJson.totalCount || 0;
      
      console.log('Fetched news data:', { newsData, totalItems, responseJson });
      
      if (!newsData || !Array.isArray(newsData)) {
        setNewsUpdates([]);
      } else {
        // 국가별 필터링 로직 추가
        newsData = newsData.filter(news => {
          if (countryCode === 'nz' && news.areaId === 0) return true;
          if (countryCode === 'au' && news.areaId === 201) return true;
          if (countryCode === 'ca' && news.areaId === 301) return true;
          
          // 그 외의 경우, 해당 국가의 지역 ID인지 확인
          const location = locations.find(loc => 
            loc.id === news.areaId && 
            loc.countryCode.toLowerCase() === countryCode.toLowerCase()
          );
          return !!location;
        });
        setNewsUpdates(newsData);
      }
    } catch (err) {
      console.error('Error fetching news updates:', err);
      setError('Failed to fetch news updates');
    } finally {
      setLoading(false);
    }
  }, [locationId, viewFilter, countryCode, locations]);

  useEffect(() => {
    fetchNewsUpdates();
  }, [locationId, viewFilter, fetchNewsUpdates]);

  // 선택된 데이터가 변경될 때 입력 필드만 업데이트
  useEffect(() => {
    if (selectedData) {
      console.log('Selected data in NewsUpdate:', selectedData);

      // Batch all updates together
      const updates = () => {
        sourceRef.current.value = selectedData.source || '';
        titleRef.current.value = selectedData.title || '';
        descriptionRef.current.value = selectedData.description || '';
        linkRef.current.value = selectedData.link || '';
        if (selectedData.locationId) {
          console.log('Setting locationId:', selectedData.locationId);
          setSelectedAreaId(selectedData.locationId);
        }
      };

      updates();
    }
  }, [selectedData?.id]); // Only re-run when selectedData.id changes

  const onTranslateTitleClicked = (text) => {
    if (!text?.trim()) {
      alert('No text to translate.');
      return;
    }
    getTranslate(text)
      .then((result) => {
        if (titleRef.current) titleRef.current.value = result;
      })
      .catch((err) => {
        console.error('Translate title error:', err);
        alert(`Translation failed: ${err.message || 'Unknown error'}`);
      });
  };

  const onTranslateDescriptionClicked = (text) => {
    if (!text?.trim()) {
      alert('No text to translate.');
      return;
    }
    getTranslate(text)
      .then((result) => {
        if (descriptionRef.current) descriptionRef.current.value = result;
      })
      .catch((err) => {
        console.error('Translate description error:', err);
        alert(`Translation failed: ${err.message || 'Unknown error'}`);
      });
  };

  const handleShare = async () => {
    // Validate required fields
    if (!selectedAreaId) {
      alert("Please select an area.");
      return;
    }
    if (!titleRef.current.value.trim()) {
      alert("Please enter a title.");
      return;
    }
    if (!linkRef.current.value.trim()) {
      alert("Please enter a link.");
      return;
    }

    try {
      const shareData = {
        selectedAreaId,
        source: sourceRef.current.value.trim(),
        title: titleRef.current.value.trim(),
        description: descriptionRef.current.value.trim(),
        link: linkRef.current.value.trim()
      };

      console.log('Attempting to share with values:', shareData);

      try {
        await onShareClicked(
          shareData.selectedAreaId,
          shareData.source,
          shareData.title,
          shareData.description,
          shareData.link
        );

        // Refresh news updates list first
        await fetchNewsUpdates();

        // Then clear form fields
        sourceRef.current.value = '';
        titleRef.current.value = '';
        descriptionRef.current.value = '';
        linkRef.current.value = '';
        setSelectedAreaId('0');

        // Show success message
        alert("News added successfully to the queue");
      } catch (refreshError) {
        console.error('Error in operations:', refreshError);
        alert("An error occurred. Please check if the news was added successfully.");
      }
    } catch (error) {
      console.error('Error in handleShare:', error);
      alert("An error occurred while adding news to the queue. Please check the console for details.");
    }
  };

  const handleToggleViewed = async (id) => {
    try {
      const response = await fetch(getToogleViewedNewsUpdateUrl(id), {
        method: 'PUT',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
          "token": TOKEN,
          "Content-Type": 'application/x-www-form-urlencoded'
        },
        body: {}
      });

      if (!response.ok) {
        throw new Error('Failed to toggle viewed status');
      }

      alert('Viewed status updated successfully');
      fetchNewsUpdates();
    } catch (err) {
      console.error('Error toggling viewed status:', err);
      alert('Failed to update viewed status');
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(getDeleteNewsUpdateUrl(id), {
        method: 'DELETE',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
          "token": TOKEN,
          "Content-Type": 'application/x-www-form-urlencoded'
        },
        body: {}
      });

      if (!response.ok) {
        throw new Error('Failed to delete news update');
      }
      fetchNewsUpdates();
    } catch (err) {
      console.error('Error deleting news update:', err);
      alert('Failed to delete news update');
    }
  };

  const handleCopy = async (id) => {
    const item = newsUpdates.find(news => news.id === id);
    if (!item) {
      alert('Item not found');
      return;
    }

    try {
      const response = await fetch(getCopyNewsUpdateUrl(), {
        method: 'POST',
        headers: {
          "token": TOKEN,
          "Content-Type": 'application/json'
        },
        body: JSON.stringify({
          areaId: item.areaId,
          source: item.source || '',
          title: item.title || '',
          description: item.description || '',
          link: item.link || ''
        })
      });

      if (!response.ok) {
        throw new Error('Failed to copy news update');
      }

      alert('News copied successfully');
      fetchNewsUpdates();
    } catch (err) {
      console.error('Error copying news update:', err);
      alert('Failed to copy news update');
    }
  };

  const handleEditStart = (news) => {
    setEditingNewsId(news.id);
    setEditingTitle(news.title || '');
    setEditingDescription(news.description || '');
  };

  const handleEditCancel = () => {
    setEditingNewsId(null);
    setEditingTitle('');
    setEditingDescription('');
  };

  const handleContentUpdate = async (id) => {
    const trimmedTitle = editingTitle.trim();
    const trimmedDescription = editingDescription.trim();

    if (!trimmedTitle) {
      alert('Please enter a title.');
      return;
    }

    try {
      setSavingNewsId(id);
      const response = await fetch(getUpdateNewsContentUrl(id), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'token': TOKEN
        },
        body: JSON.stringify({
          title: trimmedTitle,
          description: trimmedDescription
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update news content');
      }

      const updatedNews = await response.json();
      setNewsUpdates((prevNewsUpdates) =>
        prevNewsUpdates.map((news) =>
          news.id === id
            ? { ...news, title: updatedNews.title, description: updatedNews.description }
            : news
        )
      );
      handleEditCancel();
      alert('News content updated successfully');
    } catch (err) {
      console.error('Error updating news content:', err);
      alert('Failed to update news content');
    } finally {
      setSavingNewsId(null);
    }
  };

  const handleViewFilterChange = (event) => {
    const value = event.target.value;
    setViewFilter(value);
  };

  const handleSelect = (selectedData) => {
    console.log('handleSelect called with:', selectedData); // 디버깅을 위한 로그 추가
    sourceRef.current.value = selectedData.source || '';
    titleRef.current.value = selectedData.title || '';
    linkRef.current.value = selectedData.link || '';
    if (selectedData.locationId) {
      console.log('Setting locationId in handleSelect:', selectedData.locationId); // 디버깅을 위한 로그 추가
      setSelectedAreaId(selectedData.locationId);
    }
  };

  const handleLocationChange = async (newsId, newAreaId) => {
    try {
      const response = await fetch(getUpdateNewsLocationUrl(newsId), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'token': TOKEN
        },
        body: JSON.stringify({ areaId: newAreaId })
      });

      if (!response.ok) {
        throw new Error('Failed to update location');
      }

      alert('Location updated successfully');
      fetchNewsUpdates();
    } catch (err) {
      console.error('Error updating location:', err);
      alert('Failed to update location');
    }
  };

  return (
    <section className="newsupdate-section">
      {loading && <div>Loading...</div>}
      {error && <div>Error: {error}</div>}
      <div className="news-list">
        <div className="news-controls">
          <h3>News Updates List</h3>
          <div className="filter-controls">
            <select value={viewFilter} onChange={handleViewFilterChange}>
              <option value="unviewed">Unviewed Only</option>
              <option value="viewed">Viewed Only</option>
              <option value="all">All News</option>
            </select>
          </div>
        </div>
        {newsUpdates && newsUpdates.length > 0 ? (
          <>
            <div className="news-items">
              {newsUpdates.map((news) => {
                const isEditing = editingNewsId === news.id;
                const isSaving = savingNewsId === news.id;

                return (
                <div key={news.id} className="news-item">
                  <div className="news-content">
                    <div className="action-buttons">
                      {isEditing ? (
                        <>
                          <button
                            className="save-button"
                            onClick={() => handleContentUpdate(news.id)}
                            disabled={isSaving}
                          >
                            {isSaving ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            className="cancel-button"
                            onClick={handleEditCancel}
                            disabled={isSaving}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button className="copy-button" onClick={() => handleCopy(news.id)}>Copy</button>
                          <button className="edit-button" onClick={() => handleEditStart(news)}>Edit</button>
                          <button className="delete-button" onClick={() => handleDelete(news.id)}>Delete</button>
                        </>
                      )}
                    </div>
                    <div className="news-header">
                      <div className="news-meta">
                        <div className="news-status">Status: {news.viewed ? 'Viewed' : 'Unviewed'}</div>
                        <div className="news-link">
                          <a href={news.link} target="_blank" rel="noopener noreferrer">View Source</a>
                        </div>
                      </div>
                    </div>
                    <div className="news-source">{news.source}</div>
                    {isEditing ? (
                      <>
                        <textarea
                          className="news-edit-field news-edit-title"
                          rows="2"
                          value={editingTitle}
                          onChange={(e) => setEditingTitle(e.target.value)}
                        />
                        <textarea
                          className="news-edit-field news-edit-description"
                          rows="4"
                          value={editingDescription}
                          onChange={(e) => setEditingDescription(e.target.value)}
                        />
                      </>
                    ) : (
                      <>
                        <div className="news-title">{news.title}</div>
                        {news.description && <div className="news-description">{news.description}</div>}
                      </>
                    )}
                    <div className="location-status-container">
                      <div className={`news-location ${!news.areaId ? 'error' : ''}`}>
                        <div className="location-controls">
                          <div className="location-wrapper">
                            <select
                              value={news.areaId || ''}
                              onChange={(e) => handleLocationChange(news.id, e.target.value)}
                              className="location-select">
                              {locations
                                .filter(location => location.countryCode.toLowerCase() === countryCode.toLowerCase())
                                .map(location => (
                                  <option key={location.id} value={location.id}>
                                    {languageCode === 'ko' ? location.name_ko : location.name_en}
                                  </option>
                                ))}
                            </select>
                            <span className="location-label">Location: {getLocationName(news.areaId)}</span>
                          </div>
                          <button 
                            className={`toggle-viewed-button ${news.viewed ? 'viewed' : ''}`} 
                            onClick={() => handleToggleViewed(news.id)}
                            title={news.viewed ? 'Mark as Unviewed' : 'Mark as Viewed'}
                          >
                            {news.viewed ? '✓ Viewed' : 'Mark as Viewed'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )})}
            </div>
          </>
        ) : (
          <p>No news updates available.</p>
        )}
      </div>

      <div className="newsupdate-container">
        <div className="input-group location-source-group">
          <select
            value={selectedAreaId}
            onChange={(e) => setSelectedAreaId(e.target.value)}
            className="custom-select">
            {locations
              .filter(location => location.countryCode.toLowerCase() === countryCode.toLowerCase())
              .map(location => (
                <option key={location.id} value={location.id}>
                  {languageCode === 'ko' ? location.name_ko : location.name_en}
                </option>
              ))}
          </select>
          <textarea
            ref={sourceRef}
            name="source"
            placeholder="Source"
            rows="1"
            className="custom-textarea single-line-textarea" />
        </div>
        <div className="input-group">
          <textarea
            ref={titleRef}
            name="title"
            placeholder="Title"
            rows="1"
            className="custom-textarea single-line-textarea" />
          <button
            className="translate-button"
            onClick={() => onTranslateTitleClicked(titleRef.current.value)}>
            Translate
          </button>
        </div>
        <div className="input-group">
          <textarea
            ref={descriptionRef}
            name="description"
            placeholder="Description"
            rows="2"
            className="custom-textarea" />
          <button
            className="translate-button"
            onClick={() => onTranslateDescriptionClicked(descriptionRef.current.value)}>
            Translate
          </button>
        </div>
        <div className="input-group">
          <textarea
            ref={linkRef}
            name="link"
            placeholder="Link"
            rows="1"
            className="custom-textarea single-line-textarea" />
          <button className="confirm-button" onClick={handleShare}>Confirm</button>
        </div>
      </div>
    </section>
  );
};

export default NewsUpdate;
